import {
    ImportMeshAsync,
    TransformNode,
    Vector3,
    Quaternion,
    PointerEventTypes,
} from "@babylonjs/core";
import "@babylonjs/loaders";

/**
 * createRailRobotAssemblyBinding
 * ------------------------------------------------------------
 * 단일 조립 GLB용 바인딩 함수.
 *
 * 예상 GLB 구조:
 *
 * RailRobotAssembly
 * ├─ RailStaticRoot
 * │  └─ Base912
 * │     └─ 고정 레일 / 브라켓 / 지지부
 * └─ RailMovingRoot
 *    └─ Moving Plate911
 *       ├─ Robot Base909
 *       │  └─ RobotArmMount_Base909
 *       │     └─ UR3e
 *       │        └─ base
 *       │           └─ shoulder
 *       │              └─ upperarm
 *       │                 └─ forearm
 *       │                    └─ wrist1
 *       │                       └─ wrist2
 *       │                          └─ wrist3
 *       └─ 이동 플레이트 주변 부품
 *
 * 핵심:
 * - assemblyGlbUrl 하나만 로드한다.
 * - RailMovingRoot를 움직이면 이동부 + Robot Base909 + UR3e가 같이 이동한다.
 * - UR3e의 base/shoulder/upperarm/forearm/wrist1/wrist2/wrist3 노드는 유지한다.
 * - ur3e_joints.json 기준으로 각 관절 childNode를 찾아 회전시킨다.
 * - motion.csv에 rail_offset_x / rail_position_x / rail_progress 컬럼이 있으면 레일 이동도 같이 재생한다.
 */

const COMPONENT_ROBOT_ASSETS = import.meta.glob("./robot/**/*", {
  eager: true,
  query: "?url",
  import: "default",
});


function normalizeRobotAssetKey(path) {
  const clean = String(path ?? "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\.?\//, "");

  if (clean.startsWith("robot/")) {
    return `./${clean}`;
  }

  return `./robot/${clean}`;
}

function isExternalOrAbsoluteUrl(path) {
  return /^(https?:|blob:|data:|\/)/.test(String(path ?? "").trim());
}

function resolveComponentRobotAsset(path) {
  const raw = String(path ?? "").trim();

  // 외부 URL 또는 public 기준 절대 경로는 그대로 허용
  if (isExternalOrAbsoluteUrl(raw)) {
    return raw;
  }

  const key = normalizeRobotAssetKey(raw);
  const url = COMPONENT_ROBOT_ASSETS[key];

  if (url) {
    return url;
  }

  /**
   * dev fallback.
   * build 시에는 glob에 잡히는 파일만 안정적으로 포함됨.
   */
  if (import.meta.env.DEV) {
    console.warn("[robot asset fallback]", {
      input: path,
      normalizedKey: key,
      availableKeys: Object.keys(COMPONENT_ROBOT_ASSETS),
    });

    return new URL(key, import.meta.url).href;
  }

  throw new Error(
    `Robot asset not found in build assets: ${key}\n` +
      `Available keys:\n${Object.keys(COMPONENT_ROBOT_ASSETS).join("\n")}`
  );
}

const CONTROLLABLE_JOINTS = [
    "shoulder_pan_joint",
    "shoulder_lift_joint",
    "elbow_joint",
    "wrist_1_joint",
    "wrist_2_joint",
    "wrist_3_joint",
];

const CSV_MOTION_JOINT_COLUMNS = {
    shoulder_pan_joint: "shoulder_pan_joint_pos",
    shoulder_lift_joint: "shoulder_lift_joint_pos",
    elbow_joint: "elbow_joint_pos",
    wrist_1_joint: "wrist_1_joint_pos",
    wrist_2_joint: "wrist_2_joint_pos",
    wrist_3_joint: "wrist_3_joint_pos",
};

const CSV_RAIL_MOTION_COLUMNS = {
    offset: [
        "rail_offset",
        "rail_offset_x",
        "rail_offset_y",
        "rail_offset_z",
        "rail_moving_offset",
        "carriage_offset",
        "carriage_offset_x",
    ],
    position: [
        "rail_position",
        "rail_position_x",
        "rail_position_y",
        "rail_position_z",
        "rail_pos",
        "rail_x",
        "carriage_position",
        "carriage_position_x",
    ],
    progress: [
        "rail_progress",
        "carriage_progress",
    ],
};

const DEFAULT_JOINT_LIMITS = {
    shoulder_pan_joint: { lower: -Math.PI * 2, upper: Math.PI * 2 },
    shoulder_lift_joint: { lower: -Math.PI * 2, upper: Math.PI * 2 },
    elbow_joint: { lower: -Math.PI, upper: Math.PI },
    wrist_1_joint: { lower: -Math.PI * 2, upper: Math.PI * 2 },
    wrist_2_joint: { lower: -Math.PI * 2, upper: Math.PI * 2 },
    wrist_3_joint: { lower: null, upper: null },
};

function toVector3(value, fallback = new Vector3(0, 0, 0)) {
    if (value == null) return fallback.clone();
    if (value instanceof Vector3) return value.clone();

    if (Array.isArray(value)) {
        return new Vector3(
            Number(value[0] ?? fallback.x),
            Number(value[1] ?? fallback.y),
            Number(value[2] ?? fallback.z)
        );
    }

    if (typeof value === "object") {
        return new Vector3(
            Number(value.x ?? fallback.x),
            Number(value.y ?? fallback.y),
            Number(value.z ?? fallback.z)
        );
    }

    return fallback.clone();
}

function isAbsoluteUrl(url) {
    return /^(https?:|blob:|data:)/i.test(String(url ?? ""));
}

function resolveUrl(urlOrPath) {
    const value = String(urlOrPath ?? "").trim();

    if (!value) return "";
    if (isAbsoluteUrl(value)) return value;
    if (value.startsWith("/")) return new URL(value, window.location.origin).href;

    return new URL(value, import.meta.url).href;
}

async function fetchText(url, { optional = false } = {}) {
    const absoluteUrl = resolveUrl(url);

    try {
        const res = await fetch(absoluteUrl);

        if (!res.ok) {
            throw new Error(`파일을 불러오지 못했습니다: ${absoluteUrl} (${res.status})`);
        }

        return await res.text();
    } catch (error) {
        if (optional) return "";
        throw error;
    }
}

async function fetchJson(url) {
    const text = await fetchText(url);
    console.log("[JSON parse target]", url, text.slice(0, 500));
    return JSON.parse(text);
}

function normalizeAxis(axis) {
    const v = toVector3(axis, new Vector3(0, 1, 0));

    if (v.lengthSquared() === 0) {
        return new Vector3(0, 1, 0);
    }

    return v.normalize();
}

function ensureRotationQuaternion(node) {
    if (!node) return Quaternion.Identity();

    if (!node.rotationQuaternion) {
        node.rotationQuaternion = Quaternion.FromEulerAngles(
            Number(node.rotation?.x ?? 0),
            Number(node.rotation?.y ?? 0),
            Number(node.rotation?.z ?? 0)
        );

        node.rotation?.set?.(0, 0, 0);
    }

    return node.rotationQuaternion.clone();
}

function collectImportedNodes(result) {
    return [
        ...(result?.transformNodes ?? []),
        ...(result?.meshes ?? []),
    ].filter(Boolean);
}

function findNodeByNameInList(nodes, name) {
    if (!name) return null;

    return (
        nodes.find((node) => node.name === name) ??
        nodes.find((node) => String(node.name ?? "").split(".")[0] === name) ??
        null
    );
}

function parseJointConfig(jointsJson) {
    const joints = Array.isArray(jointsJson?.joints) ? jointsJson.joints : [];
    const links = Array.isArray(jointsJson?.links) ? jointsJson.links : [];

    return {
        raw: jointsJson,
        links,
        joints: joints.map((joint) => {
            const jointName = joint.jointName ?? joint.name;
            const limit = joint.limit ?? DEFAULT_JOINT_LIMITS[jointName] ?? {
                lower: null,
                upper: null,
            };

            return {
                jointName,
                parentNode: joint.parentNode ?? joint.parent,
                childNode: joint.childNode ?? joint.child,
                type: joint.jointType ?? joint.type ?? "revolute",
                axis: joint.axisBabylon ?? joint.axis ?? [0, 1, 0],
                limit: {
                    lower: Number.isFinite(Number(limit.lower))
                        ? Number(limit.lower)
                        : null,
                    upper: Number.isFinite(Number(limit.upper))
                        ? Number(limit.upper)
                        : null,
                },
                sourceGlb: joint.sourceGlb ?? null,
                originUrdf: joint.originUrdf ?? null,
            };
        }),
    };
}

function parseCsvLine(line) {
    const result = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
        const char = line[i];

        if (char === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i += 1;
                continue;
            }

            inQuotes = !inQuotes;
            continue;
        }

        if (char === "," && !inQuotes) {
            result.push(current);
            current = "";
            continue;
        }

        current += char;
    }

    result.push(current);

    return result.map((item) => item.trim());
}

function findFirstColumnIndex(indexByName, candidates = []) {
    for (const columnName of candidates) {
        if (indexByName.has(columnName)) {
            return indexByName.get(columnName);
        }
    }

    return null;
}

function readOptionalNumber(values, columnIndex) {
    if (columnIndex == null) return null;

    const raw = String(values[columnIndex] ?? "").trim();
    if (raw === "") return null;

    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
}

function parseMotionCsv(csvText) {
    const lines = String(csvText ?? "")
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    if (lines.length < 2) {
        throw new Error("motion.csv에 데이터가 없습니다.");
    }

    const headers = parseCsvLine(lines[0]);
    const indexByName = new Map();

    headers.forEach((header, index) => {
        indexByName.set(header, index);
    });

    const timestampIndex = indexByName.get("timestamp_sec");

    if (timestampIndex == null) {
        throw new Error("motion.csv에 timestamp_sec 컬럼이 없습니다.");
    }

    Object.values(CSV_MOTION_JOINT_COLUMNS).forEach((columnName) => {
        if (!indexByName.has(columnName)) {
            throw new Error(`motion.csv에 ${columnName} 컬럼이 없습니다.`);
        }
    });

    const railColumnIndexes = {
        offset: findFirstColumnIndex(indexByName, CSV_RAIL_MOTION_COLUMNS.offset),
        position: findFirstColumnIndex(indexByName, CSV_RAIL_MOTION_COLUMNS.position),
        progress: findFirstColumnIndex(indexByName, CSV_RAIL_MOTION_COLUMNS.progress),
    };

    const rawFrames = [];

    for (let i = 1; i < lines.length; i += 1) {
        const values = parseCsvLine(lines[i]);
        const timestampSec = Number(values[timestampIndex]);

        if (!Number.isFinite(timestampSec)) continue;

        const pose = {};

        Object.entries(CSV_MOTION_JOINT_COLUMNS).forEach(([jointName, columnName]) => {
            const columnIndex = indexByName.get(columnName);
            const angle = Number(values[columnIndex]);

            if (Number.isFinite(angle)) {
                pose[jointName] = angle;
            }
        });

        const rail = {
            offset: readOptionalNumber(values, railColumnIndexes.offset),
            position: readOptionalNumber(values, railColumnIndexes.position),
            progress: readOptionalNumber(values, railColumnIndexes.progress),
        };

        rawFrames.push({
            timestampSec,
            pose,
            rail,
        });
    }

    if (!rawFrames.length) {
        throw new Error("motion.csv에서 유효한 프레임을 찾지 못했습니다.");
    }

    const startTime = rawFrames[0].timestampSec;

    return rawFrames.map((frame, index) => ({
        index,
        time: Math.max(0, frame.timestampSec - startTime),
        pose: frame.pose,
        rail: frame.rail,
    }));
}

function findMotionFrameIndex(frames, time) {
    if (!frames.length) return 0;

    let low = 0;
    let high = frames.length - 1;

    while (low <= high) {
        const mid = Math.floor((low + high) / 2);

        if (frames[mid].time < time) {
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    return Math.max(0, Math.min(frames.length - 1, low));
}

function interpolatePose(frameA, frameB, time) {
    if (!frameA) return {};
    if (!frameB) return { ...frameA.pose };

    const duration = frameB.time - frameA.time;

    if (duration <= 0) {
        return { ...frameA.pose };
    }

    const t = Math.min(1, Math.max(0, (time - frameA.time) / duration));
    const pose = {};

    Object.keys(CSV_MOTION_JOINT_COLUMNS).forEach((jointName) => {
        const a = Number(frameA.pose[jointName] ?? 0);
        const b = Number(frameB.pose[jointName] ?? a);

        pose[jointName] = a + (b - a) * t;
    });

    return pose;
}

function interpolateNullableNumber(a, b, t) {
    const hasA = Number.isFinite(Number(a));
    const hasB = Number.isFinite(Number(b));

    if (!hasA && !hasB) return null;
    if (hasA && !hasB) return Number(a);
    if (!hasA && hasB) return Number(b);

    return Number(a) + (Number(b) - Number(a)) * t;
}

function interpolateRailMotion(frameA, frameB, time) {
    if (!frameA) return {};

    if (!frameB) {
        return {
            ...(frameA.rail ?? {}),
        };
    }

    const duration = frameB.time - frameA.time;

    if (duration <= 0) {
        return {
            ...(frameA.rail ?? {}),
        };
    }

    const t = Math.min(1, Math.max(0, (time - frameA.time) / duration));

    return {
        offset: interpolateNullableNumber(frameA.rail?.offset, frameB.rail?.offset, t),
        position: interpolateNullableNumber(frameA.rail?.position, frameB.rail?.position, t),
        progress: interpolateNullableNumber(frameA.rail?.progress, frameB.rail?.progress, t),
    };
}

function createAnimationProfile(name, mission) {
    const key = String(name ?? mission ?? "default").toLowerCase();

    if (key.includes("welding")) {
        return {
            shoulder_pan_joint: [-0.5, 0.5],
            shoulder_lift_joint: [-1.6, -0.2],
            elbow_joint: [0.3, 1.8],
            wrist_1_joint: [-1.0, 1.0],
            wrist_2_joint: [-1.2, 1.2],
            wrist_3_joint: [-1.5, 1.5],
            intervalMin: 500,
            intervalMax: 900,
            smoothing: 3.5,
        };
    }

    if (key.includes("inspection")) {
        return {
            shoulder_pan_joint: [-1.2, 1.2],
            shoulder_lift_joint: [-1.4, -0.4],
            elbow_joint: [0.2, 1.5],
            wrist_1_joint: [-1.4, 1.4],
            wrist_2_joint: [-1.4, 1.4],
            wrist_3_joint: [-Math.PI, Math.PI],
            intervalMin: 900,
            intervalMax: 1600,
            smoothing: 2.2,
        };
    }

    return {
        shoulder_pan_joint: [-1.0, 1.0],
        shoulder_lift_joint: [-1.8, -0.1],
        elbow_joint: [0.1, 1.9],
        wrist_1_joint: [-1.8, 1.8],
        wrist_2_joint: [-1.8, 1.8],
        wrist_3_joint: [-Math.PI, Math.PI],
        intervalMin: 600,
        intervalMax: 1200,
        smoothing: 2.8,
    };
}

function rand(min, max) {
    return Math.random() * (max - min) + min;
}

const DEFAULT_ASSEMBLY_GLB_URL = resolveComponentRobotAsset(
  "rail_robot_assembly_master.glb"
);

const DEFAULT_JOINTS_JSON_URL = resolveComponentRobotAsset(
  "ur3e_joints.json"
);

const DEFAULT_MOTION_CSV_URL = resolveComponentRobotAsset(
  "motion.csv"
);

const DEFAULT_DESCRIPTIONS_URL = resolveComponentRobotAsset(
  "descriptions.txt"
);

console.log({
  DEFAULT_ASSEMBLY_GLB_URL,
  DEFAULT_JOINTS_JSON_URL,
  DEFAULT_MOTION_CSV_URL,
  DEFAULT_DESCRIPTIONS_URL,
});

export async function createRailRobotAssemblyBinding(scene, options = {}) {
    if (!scene) {
        throw new Error("createRailRobotAssemblyBinding: scene가 필요합니다.");
    }

    const {
        id = "rail-robot-assembly-01",
        rootName = "RailRobotAssemblyRuntimeRoot",

        assemblyGlbUrl = DEFAULT_ASSEMBLY_GLB_URL,
        jointsJsonUrl = DEFAULT_JOINTS_JSON_URL,
        motionCsvUrl = DEFAULT_MOTION_CSV_URL,
        descriptionsUrl = DEFAULT_DESCRIPTIONS_URL,

        staticRootName = "RailStaticRoot",
        movingRootName = "RailMovingRoot",
        /**
         * config 호환용.
         * 실제 우선순위는 jointsJson.rail.motionNode > railMotionNodeName > movingRootName
         */
        railMotionNodeName = null,
        robotMountName = "RobotArmMount_Base909",
        robotRootName = "UR3e",

        rootPosition = { x: 0, y: 0, z: 0 },
        rootScaling = { x: 1, y: 1, z: 1 },
        rootRotation = { x: 0, y: 0, z: 0 },

        railAxis = "x",
        // 이제 railMin/railMax는 Babylon 좌표가 아니라 레일 위 거리값
        railMin = 0.0,
        railMax = 2.5,
        // 렌더링 직후 로봇이 있어야 하는 레일 위 위치
        railInitialPosition = 0.0,
        // CSV 해석 방식: 기본은 offset
        railMotionMode = "offset",
        // CSV offset 방향 보정. 방향이 반대일 때만 -1
        railOffsetSign = 1,
        // 기존 호환용. 가능하면 사용하지 않음
        railHomePosition = null,

        enablePicking = false,
        enableKeyboard = true,
        disablePointerMovePicking = true,
        optimizeStatic = true,

        equipId = id,
        equipName = "레일 이동식 용접 로봇",
        statusText = "정상",
        mode: initialMode = "Auto",
        mission: initialMission = "Welding",

        animation = null,
    } = options;

    const runtimeRoot = new TransformNode(rootName, scene);
    runtimeRoot.position = toVector3(rootPosition);
    runtimeRoot.scaling = toVector3(rootScaling, new Vector3(1, 1, 1));
    runtimeRoot.rotation = toVector3(rootRotation);

    let disposed = false;
    let pointerObserver = null;

    let currentMode = initialMode;
    let currentMission = initialMission;
    let currentAnimationName = animation?.name ?? initialMission ?? "Welding";
    let currentAnimationSpeed = Math.max(0.1, Number(animation?.speed ?? 1));

    let randomMotionRunning = false;
    let randomMotionPaused = false;
    let randomMotionRaf = null;
    let randomMotionTimer = null;

    let csvMotionFrames = null;
    let csvMotionRunning = false;
    let csvMotionPaused = false;
    let csvMotionRaf = null;
    let csvMotionStartedAt = 0;
    let csvMotionPausedAt = 0;
    let csvMotionSpeed = 1;
    let csvMotionLoop = true;

    const loadedMeshes = [];
    const visualMeshes = [];
    const linkNodes = new Map();
    const jointRuntime = new Map();

    if (disablePointerMovePicking) {
        scene.skipPointerMovePicking = true;
    }

    const [jointsJson, descriptionsText] = await Promise.all([
        fetchJson(jointsJsonUrl),
        fetchText(descriptionsUrl, { optional: true }),
    ]);

    const jointConfig = parseJointConfig(jointsJson);

    const railMeta = jointsJson?.rail ?? {};

    /**
     * 이동 대상 노드는 JSON 메타데이터를 우선 사용한다.
     * JSON에 없으면 config의 railMotionNodeName,
     * 그것도 없으면 기존 movingRootName을 사용한다.
     */
    const effectiveMovingRootName =
        railMeta.motionNode ?? railMotionNodeName ?? movingRootName;

    /**
     * 이동축은 JSON의 axisName을 우선 사용한다.
     * railDirection 같은 수동 벡터는 사용하지 않는다.
     */
    const effectiveRailAxis = String(
        railMeta.axisName ?? railAxis ?? "x"
    ).toLowerCase();

    /**
     * 방향 보정값은 Babylon 좌표 적용 단계에서만 사용한다.
     */
    const resolvedRailOffsetSign =
        Number(railOffsetSign) < 0 ? -1 : 1;

    const absoluteGlbUrl = resolveUrl(assemblyGlbUrl);
    const importResult = await ImportMeshAsync(absoluteGlbUrl, scene);

    const importedNodes = collectImportedNodes(importResult);
    const importedNodeSet = new Set(importedNodes);

    const topLevelNodes = importedNodes.filter((node) => {
        return !node.parent || !importedNodeSet.has(node.parent);
    });

    topLevelNodes.forEach((node) => {
        node.parent = runtimeRoot;
    });

    const staticRoot =
        findNodeByNameInList(importedNodes, staticRootName) ??
        scene.getNodeByName(staticRootName);

    const movingRoot =
        findNodeByNameInList(importedNodes, effectiveMovingRootName) ??
        scene.getNodeByName(effectiveMovingRootName);

    const robotMount =
        findNodeByNameInList(importedNodes, robotMountName) ??
        scene.getNodeByName(robotMountName);

    const robotRoot =
        findNodeByNameInList(importedNodes, robotRootName) ??
        scene.getNodeByName(robotRootName);

    const equipmentMeta = {
        id,
        equipId,
        equipName,
        statusText,
        mode: currentMode,
        mission: currentMission,
        type: "railRobotAssembly",
    };

    (importResult.meshes ?? [])
        .filter((mesh) => mesh && mesh.name !== "__root__")
        .forEach((mesh) => {
            mesh.isPickable = enablePicking;
            mesh.metadata = {
                ...(mesh.metadata ?? {}),
                ...equipmentMeta,
                partName: mesh.name,
            };

            loadedMeshes.push(mesh);
            visualMeshes.push(mesh);
        });

    if (optimizeStatic && staticRoot) {
        staticRoot.getChildMeshes(false).forEach((mesh) => {
            mesh.isPickable = false;
            mesh.freezeWorldMatrix();
            mesh.doNotSyncBoundingInfo = true;
        });
    }

    const initialMovingPosition = movingRoot?.position?.clone?.() ?? Vector3.Zero();

    function readMovingAxisValue(position = movingRoot?.position) {
        if (!position) return 0;

        if (effectiveRailAxis === "x") return Number(position.x ?? 0);
        if (effectiveRailAxis === "y") return Number(position.y ?? 0);
        if (effectiveRailAxis === "z") return Number(position.z ?? 0);

        return 0;
    }

    function writeMovingAxisValue(position, value) {
        if (!position) return;

        if (effectiveRailAxis === "x") {
            position.x = value;
        } else if (effectiveRailAxis === "y") {
            position.y = value;
        } else if (effectiveRailAxis === "z") {
            position.z = value;
        }
    }

    const resolvedRailInitialPosition =
        Number.isFinite(Number(railInitialPosition))
            ? Number(railInitialPosition)
            : Number.isFinite(Number(railHomePosition))
                ? Number(railHomePosition)
                : 0;

    jointConfig.links.forEach((linkName) => {
        const node =
            findNodeByNameInList(importedNodes, linkName) ??
            scene.getNodeByName(linkName);

        if (node) {
            linkNodes.set(linkName, node);
            node.metadata = {
                ...(node.metadata ?? {}),
                ...equipmentMeta,
                linkName,
                assetMode: "link-node",
            };
        }
    });

    jointConfig.joints.forEach((joint) => {
        const childNode =
            linkNodes.get(joint.childNode) ??
            findNodeByNameInList(importedNodes, joint.childNode) ??
            scene.getNodeByName(joint.childNode);

        if (!joint.jointName || !childNode) {
            return;
        }

        if (!linkNodes.has(joint.childNode)) {
            linkNodes.set(joint.childNode, childNode);
        }

        const axis = normalizeAxis(joint.axis);

        jointRuntime.set(joint.jointName, {
            name: joint.jointName,
            type: joint.type,
            parentNodeName: joint.parentNode,
            childNodeName: joint.childNode,
            childNode,
            motionNode: childNode,
            originNode: childNode.parent ?? robotRoot ?? runtimeRoot,
            initialPosition: childNode.position?.clone?.() ?? Vector3.Zero(),
            initialRotationQuaternion: ensureRotationQuaternion(childNode),
            axis,
            limit: joint.limit,
            currentAngle: 0,
            targetAngle: 0,
            sourceGlb: joint.sourceGlb,
            originUrdf: joint.originUrdf,
        });
    });

    function updateMeshMetadata() {
        loadedMeshes.forEach((mesh) => {
            if (!mesh) return;

            mesh.metadata = {
                ...(mesh.metadata ?? {}),
                mode: currentMode,
                mission: currentMission,
                animationName: currentAnimationName,
            };
        });
    }

    function clampRailScalar(value) {
        const numericValue = Number(value);
        const numericMin = Number(railMin);
        const numericMax = Number(railMax);

        const safeValue = Number.isFinite(numericValue)
            ? numericValue
            : resolvedRailInitialPosition;

        const safeMin = Number.isFinite(numericMin) ? numericMin : 0;
        const safeMax = Number.isFinite(numericMax) ? numericMax : safeMin;

        const min = Math.min(safeMin, safeMax);
        const max = Math.max(safeMin, safeMax);

        return Math.min(max, Math.max(min, safeValue));
    }

    /**
     * CSV offset은 "레일 위 거리 변화량"으로만 해석한다.
     *
     * 중요:
     * - railOffsetSign은 여기서 곱하지 않는다.
     * - railOffsetSign을 여기서 곱하면 railInitialPosition = 0, railMin = 0일 때
     *   음수 방향 이동이 clamp되어 0으로 막힌다.
     * - 방향 보정은 applyRailScalar()에서 실제 Babylon 축 좌표로 변환할 때 적용한다.
     */
    function getRailScalarFromOffset(offset = 0) {
        const numericOffset = Number(offset);
        const safeOffset = Number.isFinite(numericOffset) ? numericOffset : 0;

        return clampRailScalar(resolvedRailInitialPosition + safeOffset);
    }

    /**
     * 레일 위 논리 위치 scalar를 실제 movingRoot.position으로 반영한다.
     *
     * scalar:
     * - 레일 위 논리 위치
     * - 예: 0.0 ~ 2.5
     *
     * 실제 Babylon 이동:
     * - initialMovingPosition 기준
     * - effectiveRailAxis 축 하나만 이동
     * - railOffsetSign은 여기에서만 적용
     */
    function applyRailScalar(scalar = resolvedRailInitialPosition) {
        if (!movingRoot) return resolvedRailInitialPosition;

        const clampedScalar = clampRailScalar(scalar);

        /**
         * initialMovingPosition은 GLB가 처음 렌더링된 실제 Babylon 위치.
         * resolvedRailInitialPosition은 그 위치가 레일 위 몇 지점인지 나타내는 논리 스칼라.
         *
         * 예:
         * - initialMovingPosition.x = 0
         * - resolvedRailInitialPosition = 0
         * - clampedScalar = 0.2
         *
         * railOffsetSign = 1
         *   실제 x = 0 + 0.2
         *
         * railOffsetSign = -1
         *   실제 x = 0 - 0.2
         */
        const deltaFromInitial = clampedScalar - resolvedRailInitialPosition;
        const axisDeltaFromInitial = deltaFromInitial * resolvedRailOffsetSign;

        const baseAxisValue = readMovingAxisValue(initialMovingPosition);
        const nextAxisValue = baseAxisValue + axisDeltaFromInitial;

        writeMovingAxisValue(movingRoot.position, nextAxisValue);

        return clampedScalar;
    }

    /**
     * CSV rail_offset 적용.
     *
     * rail_offset은 항상 railInitialPosition 기준 상대 이동량이다.
     */
    function setRailOffset(offset = 0) {
        return applyRailScalar(getRailScalarFromOffset(offset));
    }

    /**
     * rail_position 적용.
     *
     * 이 값은 Babylon 실제 x/y/z 좌표가 아니라
     * 레일 위 논리 위치 scalar다.
     */
    function setRailPosition(position = 0) {
        return applyRailScalar(position);
    }

    /**
     * progress 적용.
     *
     * progress = 0 → railMin
     * progress = 1 → railMax
     */
    function setRailProgress(t = 0) {
        const progress = Math.min(1, Math.max(0, Number(t) || 0));
        return setRailPosition(railMin + (railMax - railMin) * progress);
    }

    /**
     * 렌더링 초기 위치로 복귀.
     */
    function resetRailPosition() {
        return applyRailScalar(resolvedRailInitialPosition);
    }

    /**
     * 현재 레일 위 논리 위치를 반환한다.
     *
     * 실제 movingRoot.position.x/y/z에서 역산한다.
     */
    function getRailPosition() {
        if (!movingRoot) return resolvedRailInitialPosition;

        const currentAxisValue = readMovingAxisValue(movingRoot.position);
        const initialAxisValue = readMovingAxisValue(initialMovingPosition);
        const axisDeltaFromInitial = currentAxisValue - initialAxisValue;

        return resolvedRailInitialPosition + axisDeltaFromInitial / resolvedRailOffsetSign;
    }

    /**
     * 현재 초기 위치 기준 offset을 반환한다.
     */
    function getRailOffset() {
        return getRailPosition() - resolvedRailInitialPosition;
    }

    /**
     * 새 구조에서 명확한 이름.
     */
    function getRailInitialPosition() {
        return resolvedRailInitialPosition;
    }

    /**
     * 기존 호환용 alias.
     */
    function getRailHomePosition() {
        return getRailInitialPosition();
    }

    function applyRailMotion(rail = {}) {
        if (!movingRoot || !rail) return null;

        if (railMotionMode === "position") {
            if (Number.isFinite(Number(rail.position))) {
                return setRailPosition(Number(rail.position));
            }

            if (Number.isFinite(Number(rail.offset))) {
                return setRailOffset(Number(rail.offset));
            }

            return null;
        }

        if (railMotionMode === "progress") {
            if (Number.isFinite(Number(rail.progress))) {
                return setRailProgress(Number(rail.progress));
            }

            if (Number.isFinite(Number(rail.offset))) {
                return setRailOffset(Number(rail.offset));
            }

            return null;
        }

        /**
         * 기본값: offset
         */
        if (Number.isFinite(Number(rail.offset))) {
            return setRailOffset(Number(rail.offset));
        }

        if (Number.isFinite(Number(rail.position))) {
            return setRailPosition(Number(rail.position));
        }

        if (Number.isFinite(Number(rail.progress))) {
            return setRailProgress(Number(rail.progress));
        }

        return null;
    }

    function applyJointAngle(jointName, angle) {
        const runtime = jointRuntime.get(jointName);
        if (!runtime) return 0;

        let nextAngle = Number(angle) || 0;

        if (runtime.type !== "continuous" && runtime.type !== "revolute") {
            nextAngle = 0;
        }

        if (runtime.type === "revolute") {
            if (runtime.limit.lower != null) {
                nextAngle = Math.max(runtime.limit.lower, nextAngle);
            }

            if (runtime.limit.upper != null) {
                nextAngle = Math.min(runtime.limit.upper, nextAngle);
            }
        }

        runtime.currentAngle = nextAngle;

        const deltaQuaternion = Quaternion.RotationAxis(runtime.axis, nextAngle);
        runtime.childNode.rotationQuaternion =
            runtime.initialRotationQuaternion.multiply(deltaQuaternion);

        return nextAngle;
    }

    function setJointAngle(jointName, angle) {
        return applyJointAngle(jointName, angle);
    }

    function rotateJoint(jointName, delta) {
        const runtime = jointRuntime.get(jointName);
        if (!runtime) return 0;

        return applyJointAngle(jointName, runtime.currentAngle + delta);
    }

    function getPose() {
        const pose = {};

        CONTROLLABLE_JOINTS.forEach((jointName) => {
            pose[jointName] = jointRuntime.get(jointName)?.currentAngle ?? 0;
        });

        return pose;
    }

    function setPose(pose = {}) {
        Object.entries(pose).forEach(([jointName, angle]) => {
            if (jointRuntime.has(jointName)) {
                applyJointAngle(jointName, angle);
            }
        });
    }

    function resetPose() {
        CONTROLLABLE_JOINTS.forEach((jointName) => {
            applyJointAngle(jointName, 0);
        });
    }

    async function loadCsvMotionFrames() {
        if (csvMotionFrames) return csvMotionFrames;

        const csvText = await fetchText(motionCsvUrl);
        csvMotionFrames = parseMotionCsv(csvText);

        return csvMotionFrames;
    }

    function stopCsvMotion({ reset = false } = {}) {
        csvMotionRunning = false;
        csvMotionPaused = false;
        csvMotionStartedAt = 0;
        csvMotionPausedAt = 0;

        if (csvMotionRaf) {
            cancelAnimationFrame(csvMotionRaf);
            csvMotionRaf = null;
        }

        if (reset) {
            resetPose();
            resetRailPosition();
        }
    }

    function animateCsvMotion(now) {
        if (disposed || !csvMotionRunning || !csvMotionFrames?.length) {
            return;
        }

        if (csvMotionPaused) {
            csvMotionRaf = requestAnimationFrame(animateCsvMotion);
            return;
        }

        const frames = csvMotionFrames;
        const duration = frames[frames.length - 1].time;

        if (duration <= 0) {
            setPose(frames[0].pose);
            applyRailMotion(frames[0].rail);
            stopCsvMotion();
            return;
        }

        const elapsed = ((now - csvMotionStartedAt) / 1000) * csvMotionSpeed;
        let motionTime = elapsed;

        if (csvMotionLoop) {
            motionTime = elapsed % duration;
        } else if (elapsed >= duration) {
            setPose(frames[frames.length - 1].pose);
            applyRailMotion(frames[frames.length - 1].rail);
            stopCsvMotion();
            return;
        }

        const nextIndex = findMotionFrameIndex(frames, motionTime);
        const prevIndex = Math.max(0, nextIndex - 1);

        const pose = interpolatePose(frames[prevIndex], frames[nextIndex], motionTime);
        const rail = interpolateRailMotion(frames[prevIndex], frames[nextIndex], motionTime);

        setPose(pose);
        applyRailMotion(rail);

        csvMotionRaf = requestAnimationFrame(animateCsvMotion);
    }

    async function playCsvMotion(options = {}) {
        const { speed = 1, loop = true, reset = true } = options;

        stopAnimation();

        const frames = await loadCsvMotionFrames();

        if (!frames.length) {
            throw new Error("CSV 모션 프레임이 없습니다.");
        }

        csvMotionSpeed = Math.max(0.01, Number(speed || 1));
        csvMotionLoop = loop !== false;
        csvMotionRunning = true;
        csvMotionPaused = false;

        if (reset) {
            setPose(frames[0].pose);
            applyRailMotion(frames[0].rail);
        }

        csvMotionStartedAt = performance.now();

        if (csvMotionRaf) {
            cancelAnimationFrame(csvMotionRaf);
        }

        csvMotionRaf = requestAnimationFrame(animateCsvMotion);
        currentAnimationName = "CSV Motion";
        updateMeshMetadata();

        return {
            stop: () => stopCsvMotion({ reset: true }),
            pause: pauseCsvMotion,
            play: resumeCsvMotion,
            restart: resumeCsvMotion,
            setSpeedRatio: setCsvMotionSpeed,
        };
    }

    function pauseCsvMotion() {
        if (!csvMotionRunning || csvMotionPaused) return;

        csvMotionPaused = true;
        csvMotionPausedAt = performance.now();
    }

    function resumeCsvMotion() {
        if (!csvMotionRunning) {
            playCsvMotion({
                speed: csvMotionSpeed,
                loop: csvMotionLoop,
                reset: false,
            });
            return;
        }

        if (!csvMotionPaused) return;

        const pausedDuration = performance.now() - csvMotionPausedAt;
        csvMotionStartedAt += pausedDuration;
        csvMotionPaused = false;
        csvMotionPausedAt = 0;
    }

    function setCsvMotionSpeed(speed = 1) {
        const nextSpeed = Math.max(0.01, Number(speed || 1));

        if (csvMotionRunning && !csvMotionPaused) {
            const now = performance.now();
            const elapsedSeconds =
                ((now - csvMotionStartedAt) / 1000) * csvMotionSpeed;

            csvMotionStartedAt = now - (elapsedSeconds / nextSpeed) * 1000;
        }

        csvMotionSpeed = nextSpeed;
        return csvMotionSpeed;
    }

    function scheduleNextTargets(profile) {
        if (randomMotionTimer) {
            clearTimeout(randomMotionTimer);
            randomMotionTimer = null;
        }

        CONTROLLABLE_JOINTS.forEach((jointName) => {
            const runtime = jointRuntime.get(jointName);
            const range = profile[jointName];

            if (!runtime || !range) return;

            let target = rand(range[0], range[1]);

            if (runtime.type === "revolute") {
                if (runtime.limit.lower != null) {
                    target = Math.max(runtime.limit.lower, target);
                }

                if (runtime.limit.upper != null) {
                    target = Math.min(runtime.limit.upper, target);
                }
            }

            runtime.targetAngle = target;
        });

        const delay = rand(profile.intervalMin, profile.intervalMax) / currentAnimationSpeed;

        randomMotionTimer = setTimeout(() => {
            if (!disposed && randomMotionRunning && !randomMotionPaused) {
                scheduleNextTargets(profile);
            }
        }, delay);
    }

    function animateRandomMotion(now) {
        if (disposed || !randomMotionRunning) return;

        const last = animateRandomMotion._last || now;
        const dt = Math.min(0.05, (now - last) / 1000);
        animateRandomMotion._last = now;

        if (!randomMotionPaused) {
            const profile = createAnimationProfile(currentAnimationName, currentMission);
            const t = Math.min(
                0.2,
                Math.max(0.02, dt * profile.smoothing * currentAnimationSpeed)
            );

            CONTROLLABLE_JOINTS.forEach((jointName) => {
                const runtime = jointRuntime.get(jointName);
                if (!runtime) return;

                const nextAngle =
                    runtime.currentAngle + (runtime.targetAngle - runtime.currentAngle) * t;

                applyJointAngle(jointName, nextAngle);
            });
        }

        randomMotionRaf = requestAnimationFrame(animateRandomMotion);
    }

    function playAnimation(payloadOrName, maybeOptions = {}) {
        const payload =
            typeof payloadOrName === "string"
                ? { name: payloadOrName, ...maybeOptions }
                : { ...(payloadOrName ?? {}) };

        stopCsvMotion({ reset: false });

        currentAnimationName =
            payload.name ?? currentAnimationName ?? currentMission ?? "Welding";

        currentAnimationSpeed = Math.max(
            0.1,
            Number(payload.speed ?? currentAnimationSpeed ?? 1)
        );

        randomMotionPaused = false;

        const profile = createAnimationProfile(currentAnimationName, currentMission);

        if (!randomMotionRunning) {
            randomMotionRunning = true;
            scheduleNextTargets(profile);
            randomMotionRaf = requestAnimationFrame(animateRandomMotion);
        } else {
            scheduleNextTargets(profile);
        }

        updateMeshMetadata();

        return {
            stop: stopAnimation,
            pause: pauseAnimation,
            play: resumeAnimation,
            restart: resumeAnimation,
            setSpeedRatio: setAnimationSpeed,
        };
    }

    function stopAnimation() {
        randomMotionRunning = false;
        randomMotionPaused = false;
        animateRandomMotion._last = 0;

        if (randomMotionTimer) {
            clearTimeout(randomMotionTimer);
            randomMotionTimer = null;
        }

        if (randomMotionRaf) {
            cancelAnimationFrame(randomMotionRaf);
            randomMotionRaf = null;
        }
    }

    function pauseAnimation() {
        if (csvMotionRunning) {
            pauseCsvMotion();
            return;
        }

        randomMotionPaused = true;
    }

    function resumeAnimation() {
        if (csvMotionRunning) {
            resumeCsvMotion();
            return;
        }

        if (!randomMotionRunning) {
            playAnimation({
                name: currentAnimationName,
                speed: currentAnimationSpeed,
            });
            return;
        }

        randomMotionPaused = false;
    }

    function setAnimationSpeed(speed = 1) {
        currentAnimationSpeed = Math.max(0.1, Number(speed || 1));
        return currentAnimationSpeed;
    }

    function setMode(nextMode) {
        currentMode = nextMode ?? currentMode;
        updateMeshMetadata();
        return currentMode;
    }

    function setMission(nextMission) {
        currentMission = nextMission ?? currentMission;
        updateMeshMetadata();
        return currentMission;
    }

    function focusTarget() {
        const camera = scene.activeCamera;

        if (!camera || typeof camera.setTarget !== "function") return;

        camera.setTarget((movingRoot ?? robotMount ?? runtimeRoot).getAbsolutePosition());
    }

    function select() {
        scene.metadata = {
            ...(scene.metadata || {}),
            selectedRailRobotAssemblyId: equipId,
            selectedRobotArmId: equipId,
        };
    }

    function onKeyDown(event) {
        if (!enableKeyboard || disposed) return;

        const delta = 0.05;

        switch (event.key) {
            case "1":
                rotateJoint("shoulder_pan_joint", -delta);
                break;
            case "2":
                rotateJoint("shoulder_pan_joint", delta);
                break;
            case "3":
                rotateJoint("shoulder_lift_joint", -delta);
                break;
            case "4":
                rotateJoint("shoulder_lift_joint", delta);
                break;
            case "5":
                rotateJoint("elbow_joint", -delta);
                break;
            case "6":
                rotateJoint("elbow_joint", delta);
                break;
            case "7":
                rotateJoint("wrist_1_joint", -delta);
                break;
            case "8":
                rotateJoint("wrist_1_joint", delta);
                break;
            case "9":
                rotateJoint("wrist_2_joint", -delta);
                break;
            case "0":
                rotateJoint("wrist_2_joint", delta);
                break;
            case "-":
                rotateJoint("wrist_3_joint", -delta);
                break;
            case "=":
                rotateJoint("wrist_3_joint", delta);
                break;
            default:
                break;
        }
    }

    if (enableKeyboard) {
        window.addEventListener("keydown", onKeyDown);
    }

    if (enablePicking && scene.onPointerObservable) {
        pointerObserver = scene.onPointerObservable.add((pointerInfo) => {
            if (pointerInfo.type !== PointerEventTypes.POINTERPICK) return;

            const pickedMesh = pointerInfo.pickInfo?.pickedMesh;
            if (!pickedMesh) return;
            if (pickedMesh.metadata?.equipId !== equipId) return;

            select();
        });
    }

    updateMeshMetadata();

    if (animation?.autoPlay) {
        playAnimation(animation);
    }

    return {
        root: runtimeRoot,
        rootMesh: runtimeRoot,

        staticRoot,
        movingRoot,
        robotMount,
        robotRoot,

        linkNodes,
        jointRuntime,

        meshes: loadedMeshes,
        visualMeshes,

        equipId,
        equipName,

        visualDebugInfo: {
            mode: "single-rail-robot-assembly-glb-and-joints-json",
            assemblyGlbUrl: absoluteGlbUrl,
            jointsJsonUrl: resolveUrl(jointsJsonUrl),
            motionCsvUrl: resolveUrl(motionCsvUrl),
            descriptionsUrl: resolveUrl(descriptionsUrl),
            descriptionsText,
            staticRootName: staticRoot?.name ?? null,
            movingRootName: movingRoot?.name ?? null,

            effectiveMovingRootName,
            effectiveRailAxis,
            railMin,
            railMax,
            railInitialPosition: resolvedRailInitialPosition,
            railMotionMode,
            railOffsetSign: resolvedRailOffsetSign,

            robotMountName: robotMount?.name ?? null,
            robotRootName: robotRoot?.name ?? null,
            linkNodes: [...linkNodes.keys()],
            jointNames: [...jointRuntime.keys()],
            missingJoints: CONTROLLABLE_JOINTS.filter(
                (jointName) => !jointRuntime.has(jointName)
            ),
            note:
                "조립 GLB 내부의 UR3e 링크 노드를 ur3e_joints.json 기준으로 제어합니다. RailMovingRoot 이동 시 캐리지와 로봇암이 함께 이동합니다.",
        },

        select,
        focusTarget,

        setRailOffset,
        setRailPosition,
        setRailProgress,
        resetRailPosition,
        getRailPosition,
        getRailOffset,
        getRailInitialPosition,
        getRailHomePosition,

        setJointAngle,
        rotateJoint,
        getPose,
        setPose,
        resetPose,

        playAnimation,
        stopAnimation,
        pauseAnimation,
        resumeAnimation,
        setAnimationSpeed,

        playCsvMotion,
        stopCsvMotion,
        pauseCsvMotion,
        resumeCsvMotion,
        setCsvMotionSpeed,

        setMode,
        setMission,

        dispose() {
            disposed = true;

            stopAnimation();
            stopCsvMotion({ reset: false });

            if (enableKeyboard) {
                window.removeEventListener("keydown", onKeyDown);
            }

            if (pointerObserver && scene.onPointerObservable) {
                scene.onPointerObservable.remove(pointerObserver);
                pointerObserver = null;
            }

            runtimeRoot?.dispose?.(false, true);
        },
    };
}

export default createRailRobotAssemblyBinding;